// A word on the results page that explains itself (Dane, Oct 21 call: "hover over each of those
// words or click on each of those words and see what they mean"). Tap or click toggles a small
// note under it; on a device with a mouse, hovering shows it too. Escape or a tap elsewhere
// closes it. A real <button>, so it works by keyboard and is announced.
//
// Never put one inside a <summary> or a heading: the click would toggle or announce the wrong
// thing. Put it beside them.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function Term({ children, tip, className = "" }: { children: ReactNode; tip: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const [byHover, setByHover] = useState(false);
  /** Where the note starts, in px from the word's left edge (kept on screen). */
  const [offset, setOffset] = useState(0);
  const wrap = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  /**
   * Places the note under the word but inside the screen: it starts at the word, and is pulled
   * left just enough to keep 8px clear of the right edge (or pushed right off the left edge), so
   * a phone never scrolls sideways or cuts it off.
   */
  const place = () => {
    const rect = wrap.current?.getBoundingClientRect();
    if (!rect || typeof window === "undefined") return;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const width = Math.min(288, vw - 40);
    const left = Math.max(8, Math.min(rect.left, vw - width - 8));
    setOffset(left - rect.left);
  };
  const canHover = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches === true;

  return (
    <span
      ref={wrap}
      className={`relative inline-block ${className}`}
      onMouseEnter={() => {
        if (!canHover() || open) return;
        place();
        setByHover(true);
        setOpen(true);
      }}
      onMouseLeave={() => {
        if (byHover) setOpen(false);
      }}
    >
      <button
        type="button"
        className="term cursor-help underline decoration-dotted decoration-1 underline-offset-4"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          place();
          setByHover(false);
          setOpen((o) => !(o && !byHover));
        }}
      >
        {children}
      </button>
      {open ? (
        <span
          id={id}
          role="note"
          style={{ left: offset }}
          className="absolute top-full z-30 mt-2 block w-[min(18rem,calc(100vw-2.5rem))] rounded-[12px] border border-line bg-panel p-3 text-left text-[14px] leading-snug font-normal tracking-normal text-ink normal-case shadow-lg"
        >
          {typeof tip === "string" ? <TipText text={tip} /> : tip}
        </span>
      ) : null}
    </span>
  );
}

/** A tip written as plain text: a blank line starts a new paragraph, a single newline a new line. */
function TipText({ text }: { text: string }) {
  return (
    <>
      {text.split("\n\n").map((para, i) => (
        <span key={i} className={`block ${i > 0 ? "mt-2" : ""}`}>
          {para.split("\n").map((line, j) => (
            <span key={j} className="block">
              {line}
            </span>
          ))}
        </span>
      ))}
    </>
  );
}
